/**
 * Activity Log API service
 * Handles all activity monitoring-related API calls
 */

import { apiFetch } from './index';

// ---- TypeScript Interfaces ----

export interface ActivityLog {
  activityLogId: number;
  eventType: string;
  eventCategory: string;
  entityType: string;
  entityId: number;
  actorId: number;
  actorName?: string;
  actorType: string;
  actionDescription: string;
  eventData?: Record<string, any>;
  relatedEntities?: Record<string, any>;
  educationOrganizationId?: number;
  createdAt: string;
}

export interface ActivityLogDetail extends ActivityLog {
  ipAddress?: string;
  userAgent?: string;
  educationOrganizationName?: string;
}

export interface ActivityFilters {
  search?: string;
  eventCategory?: string;
  entityType?: string;
  eventType?: string;
  startDate?: string;
  endDate?: string;
  actorId?: number;
  entityId?: number;
}

export interface PaginatedActivityLogResponse {
  data: ActivityLog[];
  page: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
}

// ---- Event category constants ----

export const EVENT_CATEGORIES = [
  'organization',
  'lea',
  'school',
  'person',
  'staff',
  'security',
  'address',
  'user',
] as const;

export const ENTITY_TYPES = [
  'educationOrganization',
  'localEducationAgency',
  'school',
  'person',
  'staff',
  'staffAssignment',
  'personAddress',
  'userAccount',
] as const;

// ---- API Functions ----

export const activityLogApi = {
  /**
   * Get paginated activity logs with filtering
   */
  async list(
    filters: ActivityFilters = {},
    page = 1,
    pageSize = 50
  ): Promise<PaginatedActivityLogResponse> {
    const params = new URLSearchParams({
      page: page.toString(),
      pageSize: pageSize.toString(),
    });

    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.append(key, value.toString());
      }
    });

    return await apiFetch<PaginatedActivityLogResponse>(
      `/activityLogs?${params}`
    );
  },

  /**
   * Get detailed information about a specific activity log entry
   */
  async getDetail(activityLogId: number): Promise<ActivityLogDetail> {
    return await apiFetch<ActivityLogDetail>(
      `/activityLogs/${activityLogId}`
    );
  },
};

// ---- Organization activity (Activity tab, Data Health report) ----

export type ActivityStatus = 'current' | 'aging' | 'stale' | 'never';
export type ActorKind = 'organization' | 'aiu' | 'system';
export type ActivityEventKind = 'all' | 'edits' | 'signIns';

export interface ActivityThresholds {
  currentWithinDays: number;
  agingWithinDays: number;
  staleContactDays: number;
}

export interface OrganizationActivitySummary {
  educationOrganizationId: number;
  leaId: number;
  name: string;
  leaType: string | null;
  windowDays: number;
  /** By days since the organization's own users last edited */
  status: ActivityStatus;
  lastEditAt: string | null;
  lastEditBy: string | null;
  lastOrganizationEditAt: string | null;
  lastOrganizationEditBy: string | null;
  organizationEdits: number;
  aiuEdits: number;
  systemEdits: number;
  editors: number;
  accountCount: number;
  signedInAccounts: number;
  neverSignedIn: number;
  contacts: number;
  roleTagGaps: number;
  staleContacts: number;
  calendarStatus: 'complete' | 'partial' | 'missing' | null;
  calendarYear: string | null;
  hasSuperintendent: boolean;
  boardMembers: number;
}

export interface OrganizationAccountActivity {
  userId: number;
  personId: number;
  name: string;
  role: string;
  isActive: boolean;
  lastSeenAt: string | null;
  signIns: number;
}

export interface OrganizationActivityDetail extends OrganizationActivitySummary {
  accounts: OrganizationAccountActivity[];
  thresholds: ActivityThresholds;
}

export interface OrganizationActivityEvent {
  activityLogId: number;
  createdAt: string;
  eventType: string;
  eventCategory: string;
  actionDescription: string;
  actorId: number;
  actorName: string | null;
  actorKind: ActorKind;
  entityType: string;
  entityId: number;
  educationOrganizationId: number | null;
  organizationName: string | null;
}

export interface PaginatedOrganizationActivityEvents {
  data: OrganizationActivityEvent[];
  page: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
}

export const organizationActivityApi = {
  /** Activity and data health for every LEA (superAdmin) */
  async listSummaries(days = 90): Promise<{ data: OrganizationActivitySummary[]; thresholds: ActivityThresholds }> {
    return await apiFetch(`/activityLogs/organizationSummaries?days=${days}`);
  },

  /** Activity, data health, and accounts for one LEA and its schools (superAdmin) */
  async getSummary(educationOrganizationId: number, days = 90): Promise<OrganizationActivityDetail> {
    return await apiFetch(`/activityLogs/organizations/${educationOrganizationId}/summary?days=${days}`);
  },

  /** Activity at one LEA and its schools, newest first (superAdmin) */
  async listEvents(
    educationOrganizationId: number,
    { days = 90, kind = 'all', page = 1, pageSize = 25 }: { days?: number; kind?: ActivityEventKind; page?: number; pageSize?: number } = {}
  ): Promise<PaginatedOrganizationActivityEvents> {
    const params = new URLSearchParams({ days: String(days), kind, page: String(page), pageSize: String(pageSize) });
    return await apiFetch(`/activityLogs/organizations/${educationOrganizationId}/events?${params}`);
  },
};
